'use client'

import { useContext, useEffect, useMemo } from 'react'
import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import { ExperimentDefinition } from '@/config/experiments.config'
import { getExperimentVariant, trackExperimentExposure } from '@/utils/experiments.utils'
import LoginContext from '@/app/_providers/LoginProvider/LoginContext'
import { QUERY_KEYS } from '@/config/tanstack-querykeys.config'

export type UseExperimentVariantOptions = {
  /**
   * Whether this user is in the experiment's audience. Resolving a variant reports an
   * exposure, so pass `false` for users who could not act on any arm — bucketing them
   * would dilute the result with users the experiment cannot possibly move.
   */
  enabled?: boolean
  /**
   * Whether the user can see their arm right now. The exposure is reported the first time
   * this is true once the variant has resolved — not on resolving, which usually happens
   * well before the arm is on screen. Leave it `true` for an arm that is live as soon as
   * it renders.
   */
  exposed?: boolean
}

export type ExperimentVariantQueryOptions = UseQueryOptions<string, Error, string, readonly unknown[]>

/**
 * The cache entry an experiment's variant lives in.
 *
 * Exported so callers that need the variant *outside* of render — an event handler that
 * must not act on the default while Amplitude is still answering — can `ensureQueryData`
 * the very same entry rather than rebuilding the key and racing their own copy of it.
 *
 * Those callers must wait for `LoginContext`'s `loading` to clear first. It reports
 * `user: null` while `/api/auth` is in flight, so a member looks exactly like a guest for
 * the first few hundred milliseconds, and `ensureQueryData` has no `enabled` gate to hold
 * it off: bucketing in that window reports an exposure against an anonymous identity, then
 * a second one under a possibly different variant once the real `personId` re-keys the query.
 *
 * @param {ExperimentDefinition} experiment - The experiment to resolve.
 * @returns {ExperimentVariantQueryOptions} Query options keyed by experiment and identity.
 */
export const useExperimentVariantQueryOptions = (
  experiment: ExperimentDefinition,
): ExperimentVariantQueryOptions => {
  const { user } = useContext(LoginContext)
  const userId = user?.profile.personId ?? undefined

  return useMemo(() => ({
    // The whole definition is part of the key: it hashes deterministically, and the
    // definitions are module constants, so the cache still hits across components.
    queryKey: [QUERY_KEYS.EXPERIMENT_VARIANT, experiment, userId],
    queryFn: () => getExperimentVariant({ experiment, userId }),
    // A user's bucket is decided once. Never refetch it, and never retry — each
    // resolution reports an exposure, so a retry would inflate the experiment.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  }), [experiment, userId])
}

/**
 * Returns the active variant for an A/B test.
 *
 * The experiment's `defaultVariant` (always the current production behavior) is
 * returned on the server and on the first client render, then replaced once the
 * variant resolves — so enabling an experiment can never cause a hydration
 * mismatch or a flash of the treatment for users who are not in it.
 *
 * Resolution is cached per experiment and identity, so any number of components can
 * ask for the same variant while Amplitude is fetched exactly once. Nothing resolves
 * until the identity to bucket on is known, so the exposure — reported once `exposed`
 * holds, and deduplicated by the SDK — lands on the user's real logged-in/guest state.
 *
 * @param {ExperimentDefinition} experiment - The experiment to resolve.
 * @param {UseExperimentVariantOptions} options - Audience gate and exposure point for the experiment.
 * @returns {string} The active variant value.
 */
const useExperimentVariant = (
  experiment: ExperimentDefinition,
  { enabled = true, exposed = true }: UseExperimentVariantOptions = {},
): string => {
  // `user` is null for a member as much as for a guest until `/api/auth` answers.
  const { loading: identityLoading } = useContext(LoginContext)

  const { data } = useQuery({
    ...useExperimentVariantQueryOptions(experiment),
    // Disabling only stops new resolutions — an already-cached variant is still
    // returned, so a user who leaves the audience mid-flow keeps the arm they were in.
    enabled: enabled && !identityLoading,
  })

  useEffect(() => {
    if (exposed && data !== undefined) trackExperimentExposure(experiment)
  }, [exposed, data, experiment])

  // Covers the server render, the first client render, and a failed resolution:
  // all three fall back to the current production behavior.
  return data ?? experiment.defaultVariant
}

export default useExperimentVariant
